import React from 'react';
import { Popover, Position, Pane, DownloadIcon } from 'evergreen-ui';
import AppButton from 'components/AppButton/AppButton';
import TargetGroupScoreDownload from '../TargetGroupScoreDownload/TargetGroupScoreDownload';
import TargetGroupFeedbackDownload from '../TargetGroupFeedbackDownload/TargetGroupFeedbackDownload';
import TargetGroupMotiviewDownload from '../TargetGroupMotiviewDownload/TargetGroupMotiviewDownload';

interface GroupDownloadActionsProps {
    isSuperAdmin: boolean;
    groupType: 'CANDIDATE' | 'INTERNAL' | 'MOTIVIEW';
    showDownloadScores: boolean;
    showDownloadFeedback: boolean;

    companyId: string;
    companyName: string;
    groupId: string;
    targetGroupName: string;
    participantsCount: number;
    scanTemplateName?: string;
    scanTemplateLanguage?: string;
}

/**
 * Consolidates the three download buttons (Scores, Feedback, Candidate Details)
 * that previously sat side-by-side in GroupHeader into a single "Download Actions"
 * dropdown button.
 *
 * Behavior:
 *   - 0 downloads available -> renders nothing
 *   - 1 download available  -> renders that single download component directly
 *                              (skips the dropdown to avoid a useless one-item menu)
 *   - 2+ downloads available -> renders one "Download Actions" button that opens a
 *                              Popover containing the available download components
 *                              stacked vertically. Each existing component still
 *                              owns its state, button, and download logic — this
 *                              wrapper does NOT touch them.
 *
 * Visibility gates match the original GroupHeader rules:
 *   - Scores:   showDownloadScores
 *   - Feedback: isSuperAdmin && showDownloadFeedback
 *   - Motiview: isSuperAdmin && groupType === 'MOTIVIEW'
 */
const GroupDownloadActions = (props: GroupDownloadActionsProps) => {
    const {
        isSuperAdmin,
        groupType,
        showDownloadScores,
        showDownloadFeedback,
        companyId,
        companyName,
        groupId,
        targetGroupName,
        participantsCount,
        scanTemplateName,
        scanTemplateLanguage,
    } = props;

    const showScores = showDownloadScores;
    const showFeedback = isSuperAdmin && showDownloadFeedback;
    const showMotiview = isSuperAdmin && groupType === 'MOTIVIEW';

    const availableCount =
        (showScores ? 1 : 0) + (showFeedback ? 1 : 0) + (showMotiview ? 1 : 0);

    if (availableCount === 0) {
        return null;
    }

    // scanTemplateName is `string` whenever showDownloadScores is true
    // (enforced by GroupHeader's discriminated union), but TypeScript loses
    // that narrowing across this component boundary, so fall back to '' here.
    // The fallback is never reached at runtime.
    const scoresAssessmentName = scanTemplateName ?? '';

    const renderScores = () => (
        <TargetGroupScoreDownload
            assessmentName={scoresAssessmentName}
            targetGroupName={targetGroupName}
            participantsCount={participantsCount}
            companyId={companyId}
            groupId={groupId}
        />
    );

    const renderFeedback = () => (
        <TargetGroupFeedbackDownload
            companyId={companyId}
            companyName={companyName}
            groupId={groupId}
            assessmentName={scanTemplateName}
            targetGroupName={targetGroupName}
            assessmentLanguage={scanTemplateLanguage}
            totalParticipants={participantsCount}
        />
    );

    const renderMotiview = () => (
        <TargetGroupMotiviewDownload
            companyId={companyId}
            companyName={companyName}
            groupId={groupId}
            assessmentName={scanTemplateName}
            assessmentLanguage={scanTemplateLanguage}
            targetGroupName={targetGroupName}
            totalParticipants={participantsCount}
        />
    );

    // Single download available — skip the dropdown and render directly.
    if (availableCount === 1) {
        if (showScores) return renderScores();
        if (showFeedback) return renderFeedback();
        if (showMotiview) return renderMotiview();
        return null;
    }

    // 2+ downloads — group them inside a Popover dropdown.
    //
    // We use Popover's function-as-child API instead of passing AppButton
    // directly. AppButton is a plain function component (no forwardRef),
    // so Popover's default cloneElement-based ref injection silently fails:
    // the React "Function components cannot be given refs" warning fires
    // and, more importantly, the toggle/positioning never wires up so
    // clicking does nothing. The function form lets us attach `getRef` to
    // a wrapper <div> and call `toggle` explicitly from onClick.
    //
    // The onClick={stopPropagation} on the content Pane is load-bearing.
    // Each inner download component swaps its button for a progress bar
    // synchronously when clicked (e.g. TargetGroupScoreDownload calls
    // setIsFetching(true)). By the time the click bubbles up to
    // document.body — where evergreen's Popover listens — the original
    // <button> target has been unmounted, so `popoverNode.contains(target)`
    // returns false. The Popover concludes the click was "external", calls
    // close(), and unmounts the whole content tree mid-download. Stopping
    // propagation at the Pane keeps body's listener from ever seeing the
    // click. Outside-click and Escape still dismiss the popover normally.
    return (
        <Popover
            position={Position.BOTTOM_RIGHT}
            content={
                <Pane
                    padding={12}
                    display="flex"
                    flexDirection="column"
                    gap={8}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    {showScores && renderScores()}
                    {showFeedback && renderFeedback()}
                    {showMotiview && renderMotiview()}
                </Pane>
            }>
            {({
                getRef,
                toggle,
            }: {
                getRef: (ref: HTMLElement | null) => void;
                toggle: () => void;
            }) => (
                <div ref={getRef as React.Ref<HTMLDivElement>}>
                    <AppButton
                        justifyContent="flex-start"
                        width="auto"
                        icon={DownloadIcon}
                        onClick={toggle}>
                        Download Actions
                    </AppButton>
                </div>
            )}
        </Popover>
    );
};

export default GroupDownloadActions;
